/**
 * @file src/components/SeoHelmet.js
 * @description Production-Ready React Helmet Async SEO Component for VelaiVendum.
 * Implements Google 2026 SEO specifications, CTR optimization, structured JSON-LD schemas,
 * OpenGraph, Twitter Cards, preconnects, and resource hints without tag duplication.
 * @module SeoHelmet
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { resolveRouteSEO } from '../seo/routeMeta';
import { DEFAULT_LOGO_URL, SITE_NAME } from '../seo/seoHelpers';

/**
 * @typedef {Object} SeoHelmetProps
 * @property {string} [title] - Title override
 * @property {string} [description] - Description override
 * @property {string} [keywords] - Keywords override
 * @property {string} [jobTitle] - Job Title for job detail pages
 * @property {string} [companyName] - Company Name for company or job detail pages
 * @property {string} [location] - Location/City name
 * @property {string} [experience] - Experience required
 * @property {string|number} [salary] - Package / Salary
 * @property {string} [employmentType] - FULL_TIME, PART_TIME, CONTRACT
 * @property {string|number} [hiringCount] - Number of openings
 * @property {string} [industry] - Industry category
 * @property {string} [image] - Custom OG image URL
 * @property {Array<{question: string, answer: string}>} [faqs] - FAQ items for FAQ schema
 * @property {Object} [config] - Config props override
 */

/**
 * High Performance SEO Component using react-helmet-async.
 *
 * @param {SeoHelmetProps} props
 * @returns {React.ReactElement}
 */
const SeoHelmet = (props = {}) => {
  const location = useLocation();
  const currentPathname = location ? location.pathname : '/';

  // Extract props or nested config
  const customData = { ...props.config, ...props };

  // Resolve dynamic metadata & structured data schemas
  const meta = resolveRouteSEO(currentPathname, customData);

  return (
    <Helmet defer={false}>
      {/* Primary Meta Tags */}
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="author" content="VelaiVendum Team" />
      <meta name="language" content="en-IN" />
      <meta name="theme-color" content="#00E785" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {/* Canonical Link */}
      <link rel="canonical" href={meta.canonical} />

      {/* Alternate Hreflang Tags */}
      <link rel="alternate" hrefLang="en-IN" href={meta.canonical} />
      <link rel="alternate" hrefLang="ta-IN" href={meta.canonical} />
      <link rel="alternate" hrefLang="x-default" href={meta.canonical} />

      {/* Performance & Core Web Vitals Resource Hints */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://checkout.razorpay.com" />
      <link rel="dns-prefetch" href="https://res.cloudinary.com" />
      <link rel="preload" as="image" href={DEFAULT_LOGO_URL} type="image/jpeg" />

      {/* OpenGraph Tags */}
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:image" content={meta.image} />
      <meta property="og:url" content={meta.canonical} />
      <meta property="og:type" content={meta.type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@VelaiVendum" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={meta.image} />

      {/* Favicon & Web App Manifest */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/logo192.png" />

      {/* JSON-LD Structured Data Objects without duplication */}
      {meta.schemas && meta.schemas.map((schemaObj, idx) => (
        <script key={`jsonld-schema-${idx}`} type="application/ld+json">
          {JSON.stringify(schemaObj)}
        </script>
      ))}
    </Helmet>
  );
};

export default React.memo(SeoHelmet);
